import { Article } from '@wix/answers-api';
import {
  UserToolbar,
  ToolbarContextSections,
  ContextData,
} from '@wix/answers-common-components';
import { useTranslation } from '@wix/wix-i18n-config';
import Long from 'long';
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { AGENT_TOOLBAR } from '../../constants';
import { useResponsive } from '../../hooks/useResponsive';
import { PageType } from '../../types';
import { useStore } from '../../zustand/stores';
import { PageData } from '../PageTemplate';

type AgentToolbarProps = {
  pageType: PageType;
  pageData: PageData;
};

const ToolbarContextSectionsMap = {
  [PageType.Article]: ToolbarContextSections.ARTICLE,
  [PageType.Category]: ToolbarContextSections.CATEGORY,
  [PageType.SearchResults]: ToolbarContextSections.NONE,
  [PageType.Homepage]: ToolbarContextSections.NONE,
  [PageType.RoadMap]: ToolbarContextSections.NONE,
  [PageType.Ticket]: ToolbarContextSections.NONE,
  [PageType.Profile]: ToolbarContextSections.NONE,
  [PageType.Contact]: ToolbarContextSections.NONE,
  [PageType.KnownIssues]: ToolbarContextSections.NONE,
};

export const AgentToolbar: FunctionComponent<AgentToolbarProps> = ({
  pageType,
  pageData,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [contextData, setContextData] = React.useState<ContextData>({});
  const { isMobile } = useResponsive();
  const { isWixEmployee: isAgent } = useStore((state) => state.userSlice);
  const showAgentToolbar = useMemo(
    () => isAgent && !isMobile,
    [isAgent, isMobile]
  );

  const formatDate = (
    date?: number | { low: number; high: number; unsigned?: boolean }
  ): string => {
    if (!date) {
      return '';
    }
    const epochMs =
      typeof date === 'number'
        ? date
        : new Long(date.low, date.high, date.unsigned).toNumber();
    return epochMs
      ? new Date(epochMs).toLocaleDateString('en-US', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })
      : '';
  };

  useEffect(() => {
    const { article, category } = pageData;
    switch (pageType) {
      case PageType.Article:
        if (article) {
          setContextData({
            articleContext: {
              article: {
                ...(article as unknown as Article),
                relatedTicketsCount: article.relatedTicketsCount || 0,
              },
              updated: formatDate(article.lastPublishDate),
              created: formatDate(article.creationDate),
              manageArticleUrl: `https://wix-bo.com/kb-backoffice-fed/articles/${article.id}/edit`,
            },
          } as unknown as ContextData);
        }
        break;
      case PageType.Category:
        if (category) {
          setContextData({
            categoryContext: {},
          });
        }
        break;
    }
  }, [pageData, pageType]);

  useEffect(() => {
    const bodyElm = document.body;
    if (showAgentToolbar) {
      if (isExpanded) {
        bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.OPEN);
        bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.CLOSE);
      } else {
        bodyElm.classList.add(AGENT_TOOLBAR.CLASSES.CLOSE);
        bodyElm.classList.remove(AGENT_TOOLBAR.CLASSES.OPEN);
      }
    }
  }, [isExpanded, showAgentToolbar]);

  const getVisibleContext = () =>
    ToolbarContextSectionsMap[pageType] || ToolbarContextSections.NONE;

  return showAgentToolbar ? (
    <UserToolbar
      isExpanded={isExpanded}
      toggleExpansion={() => setIsExpanded(!isExpanded)}
      translate={t}
      visibleContext={getVisibleContext()}
      contextData={contextData}
    />
  ) : null;
};
